import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { Icon } from "~/components/icon";
import { cn } from "~/utils";

export interface SelectOption {
  value: string;
  label: string;
}

interface ListSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  kbd?: string; // shortcut hint; a page shortcut opens this via `open`
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

// Every control in the list toolbar wears this: a bordered pill matching the
// type toggle, a monospace label, and its shortcut pinned to the right.
export const controlClass =
  "relative inline-flex h-9 shrink-0 items-center rounded-sm border border-border-contrast bg-background pl-3 pr-7 text-xs font-medium text-muted-foreground hover:bg-ghost hover:text-foreground";

export function ControlKbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="pointer-events-none absolute right-1.5 top-1/2 hidden h-4 w-4 -translate-y-1/2 items-center justify-center rounded bg-gray-100 font-mono text-[10px] leading-none text-gray-400 sm:flex">
      <span className="-translate-y-[0.5px]">{children}</span>
    </kbd>
  );
}

// A styled dropdown matching the type toggle. Radix gives keyboard nav, escape,
// and click-outside for free; `open` is controlled so a page shortcut can open it.
export function ListSelect({
  options,
  value,
  onChange,
  kbd,
  open,
  onOpenChange,
  className,
}: ListSelectProps) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            controlClass,
            "data-[state=open]:bg-ghost data-[state=open]:text-foreground",
            className,
          )}
        >
          {current?.label ?? ""}
          {kbd ? <ControlKbd>{kbd}</ControlKbd> : null}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[10rem] rounded-md border border-border bg-white p-1 text-xs shadow-lg"
        >
          {/* Report the close on selection too: the page parks its list
              shortcuts while a menu is open, and waiting only on Radix's own
              close callback can leave them parked. */}
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(next) => {
              onOpenChange?.(false);
              onChange(next);
            }}
          >
            {options.map((o) => (
              <DropdownMenu.RadioItem
                key={o.value}
                value={o.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-foreground outline-none data-[highlighted]:bg-ghost"
              >
                <span className="flex w-3.5 justify-center text-blue-600">
                  <DropdownMenu.ItemIndicator>
                    <Icon name="Check" size={12} />
                  </DropdownMenu.ItemIndicator>
                </span>
                {o.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
